import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { format } from 'date-fns';
import { publicApi } from '../services/api';
import PartnerQuotaTable from '../quotas/PartnerQuotaTable';
import { usePrivatePage } from './common';

const INACTIVE = 'This link is no longer active. Ask your Stack contact for a new one.';

/**
 * One quota view, opened by its link with no login (7k). Read-only, and nothing else: no menu,
 * no links, no account. What it shows is decided by the server: the view's name and its
 * quotas' name, description, completes, limit and progress.
 */
const SharedQuotaPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  usePrivatePage(state.view ? `${state.view.name} · Quota progress` : 'Quota progress');

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    publicApi.get(`/quota-monitors/shared/${encodeURIComponent(token)}/`)
      .then(({ data }) => setState({
        loading: false,
        loadedAt: new Date(),
        view: { name: data.name, quotas: data.quotas.map((q) => ({ ...q, limit: Number(q.limit), responses: Number(q.responses) })) },
      }))
      .catch((err) => setState({
        loading: false,
        error: err.response?.status === 404 ? INACTIVE
          : err.response?.status === 429 ? 'Too many requests just now. Wait a minute, then reload the page.'
            : "The quotas can't be loaded just now. Try again in a few minutes.",
      }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6f8' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <img src="/logo.png" alt="" style={{ width: 28, height: 28 }} />
        <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={700}>Quota progress</Typography>
      </Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {state.loading && !state.view && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>}
        {state.error && !state.view && (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, maxWidth: 560, mx: 'auto', mt: 4 }}>
            <Typography>{state.error}</Typography>
          </Paper>
        )}
        {state.view && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="h4" component="h1" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
                  {state.view.name}
                </Typography>
                <Typography color="text.secondary">As of {format(state.loadedAt, 'd MMM yyyy, HH:mm')}</Typography>
              </Box>
              <Button startIcon={<RefreshRoundedIcon />} onClick={load} disabled={state.loading} sx={{ textTransform: 'none' }}>
                {state.loading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Box>
            <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 3, p: 3 }}>
              <PartnerQuotaTable quotas={state.view.quotas} />
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default SharedQuotaPage;
